import { UseQueryResult } from '@tanstack/react-query';

import {
  apiFrontendInternalLinkingCityRetrieve,
  getApiFrontendInternalLinkingCityRetrieveQueryKey,
  useApiFrontendInternalLinkingCityRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-internal-linking-city-retrieve';
import {
  apiFrontendInternalLinkingNeighborhoodRetrieve,
  getApiFrontendInternalLinkingNeighborhoodRetrieveQueryKey,
  useApiFrontendInternalLinkingNeighborhoodRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-internal-linking-neighborhood-retrieve';
import {
  apiFrontendInternalLinkingRegionRetrieve,
  getApiFrontendInternalLinkingRegionRetrieveQueryKey,
  useApiFrontendInternalLinkingRegionRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-internal-linking-region-retrieve';
import type { ApiFrontendInternalLinkingCityRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingCityRetrieve200';
import type { ApiFrontendInternalLinkingNeighborhoodRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingNeighborhoodRetrieve200';
import type { ApiFrontendInternalLinkingRegionRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendInternalLinkingRegionRetrieve200';
import { ErrorType } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';
import type { Locality } from '@rover/types/src/Locality';

type InternalLinkingResponse =
  | ApiFrontendInternalLinkingRegionRetrieve200
  | ApiFrontendInternalLinkingCityRetrieve200
  | ApiFrontendInternalLinkingNeighborhoodRetrieve200;

export const getInternalLinkingPrefetchQueryOptions = ({
  country,
  region,
  city,
  neighborhood,
}: Locality): {
  queryKey: readonly string[];
  queryFn: () => Promise<InternalLinkingResponse>;
} => {
  if (neighborhood) {
    return {
      queryKey: getApiFrontendInternalLinkingNeighborhoodRetrieveQueryKey(
        country,
        region,
        city,
        neighborhood
      ),
      queryFn: () =>
        apiFrontendInternalLinkingNeighborhoodRetrieve(country, region, city, neighborhood),
    };
  }
  if (city) {
    return {
      queryKey: getApiFrontendInternalLinkingCityRetrieveQueryKey(country, region, city),
      queryFn: () => apiFrontendInternalLinkingCityRetrieve(country, region, city),
    };
  }
  return {
    queryKey: getApiFrontendInternalLinkingRegionRetrieveQueryKey(country, region),
    queryFn: () => apiFrontendInternalLinkingRegionRetrieve(country, region),
  };
};

export const useInternalLinking = ({
  country,
  region,
  city,
  neighborhood,
}: Locality): UseQueryResult<InternalLinkingResponse, ErrorType<unknown>> => {
  const cityEnabled = city && region && !neighborhood;
  const neighborhoodEnabled = city && region && neighborhood;
  const regionEnabled = !city && region && !neighborhood;

  const cityResponse = useApiFrontendInternalLinkingCityRetrieve(country, region, city!, {
    query: { enabled: !!cityEnabled },
  });
  const neighborhoodResponse = useApiFrontendInternalLinkingNeighborhoodRetrieve(
    country,
    region,
    city!,
    neighborhood!,
    {
      query: { enabled: !!neighborhoodEnabled },
    }
  );
  const regionResponse = useApiFrontendInternalLinkingRegionRetrieve(country, region, {
    query: { enabled: !!regionEnabled },
  });

  if (neighborhoodEnabled) {
    return neighborhoodResponse;
  }
  if (cityEnabled) {
    return cityResponse;
  }
  return regionResponse;
};
